import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ChevronRight, AlertTriangle, TrendingUp, CheckCircle2, CircleDashed, Circle } from "lucide-react";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { deleteAccount, deleteSession, listSessions, progressSummary, progressTasks } from "@/lib/api";
import { levelLabel } from "@/lib/labels";

export default function Progress() {
  const { lang, tr } = useLang();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelId, setConfirmDelId] = useState(null);
  const [confirmDelAccount, setConfirmDelAccount] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [sess, sum, tk] = await Promise.all([listSessions(), progressSummary(), progressTasks().catch(() => ({ tasks: [] }))]);
      setSessions(sess);
      setSummary(sum);
      setTasks(tk.tasks || []);
    } catch (err) {
      toast.error(tr("errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line
  }, []);

  const doDeleteSession = async () => {
    const id = confirmDelId;
    setConfirmDelId(null);
    try {
      await deleteSession(id);
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    }
  };

  const doDeleteAccount = async () => {
    setConfirmDelAccount(false);
    try {
      await deleteAccount();
      logout();
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    }
  };

  return (
    <AppShell>
      <section className="px-5 pt-4 pb-10 space-y-5" data-testid="progress-screen">
        <h1 className="text-[22px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="progress-title">
          {tr("progressTitle")}
        </h1>

        {summary && (
          <div className="wpe-card space-y-3" data-testid="progress-summary-card">
            <h2 className="wpe-eyebrow flex items-center gap-1">
              <TrendingUp size={12} /> {tr("progressSummary")}
            </h2>
            <div className="grid grid-cols-3 gap-2 text-center">
              <StatCell label={tr("progressCompleted")} value={summary.completed_count} testid="stat-completed" />
              <StatCell label={tr("progressInProgress")} value={summary.in_progress_count} testid="stat-in-progress" />
              <StatCell label={tr("progressSaved")} value={summary.saved_count} testid="stat-saved" />
            </div>

            <div className="space-y-1.5">
              <h3 className="wpe-eyebrow">{tr("recentPhrases")}</h3>
              {summary.recent_phrases.length === 0 ? (
                <div className="text-[12px] text-[rgb(var(--wpe-muted))]" data-testid="recent-phrases-empty">
                  —
                </div>
              ) : (
                <ul className="space-y-1.5" data-testid="recent-phrases-list">
                  {summary.recent_phrases.slice(0, 5).map((p, i) => (
                    <li key={i} className="text-[12px]">
                      <div className="text-[rgb(var(--wpe-navy))] font-medium">{p.english}</div>
                      {p.spanish && (
                        <div className="text-[11px] text-[rgb(var(--wpe-muted))] italic">{p.spanish}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="space-y-1.5">
              <h3 className="wpe-eyebrow">{tr("checkpointsImproved")}</h3>
              {summary.not_enough_practice || summary.checkpoints_improved.length === 0 ? (
                <div className="text-[12px] text-[rgb(var(--wpe-muted))]" data-testid="checkpoints-improved-empty">
                  {tr("notEnoughPractice")}
                </div>
              ) : (
                <ul className="space-y-1" data-testid="checkpoints-improved-list">
                  {summary.checkpoints_improved.slice(0, 6).map((c, i) => (
                    <li key={i} className="text-[12px] text-[rgb(var(--wpe-navy))] flex gap-2">
                      <span className="text-[rgb(var(--wpe-teal))]">✓</span>
                      {lang === "en" ? c.label_en : c.label_es}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {!loading && sessions.length === 0 && (
          <div className="wpe-card text-[13px] text-[rgb(var(--wpe-muted))]" data-testid="no-history">
            {tr("noHistory")}
          </div>
        )}

        {tasks.length > 0 && (
          <div className="wpe-card wpe-card-mint space-y-3" data-testid="progress-tasks-card">
            <div className="space-y-1">
              <h2 className="wpe-eyebrow-teal">{tr("progressTasksTitle")}</h2>
              <p className="text-[11px] text-[rgb(var(--wpe-muted))]" data-testid="progress-tasks-note">
                {tr("progressTasksNote")}
              </p>
            </div>
            <ul className="space-y-2" data-testid="progress-tasks-list">
              {tasks.map((t) => (
                <li key={`${t.scenario_id}-${t.attempt_level}`} data-testid={`progress-task-${t.scenario_id}-${t.attempt_level}`}>
                  <button
                    type="button"
                    onClick={() => navigate(`/feedback/${t.session_id}`)}
                    className="w-full text-left rounded-xl border border-[rgb(var(--wpe-border))] bg-white px-3 py-2.5 hover:shadow-sm hover:-translate-y-0.5 transition-all"
                    data-testid={`progress-task-open-${t.scenario_id}-${t.attempt_level}`}
                  >
                    <div className="flex items-start gap-2">
                      <StatusIcon status={t.status} />
                      <div className="flex-1 min-w-0">
                        <div
                          className="text-[13px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug"
                          data-testid={`progress-task-title-${t.scenario_id}-${t.attempt_level}`}
                        >
                          {lang === "en" ? t.title_en : t.title_es}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-[rgb(var(--wpe-muted))]">
                          <span data-testid={`progress-task-status-${t.scenario_id}-${t.attempt_level}`}>
                            {tr(`progressStatus_${t.status}`)}
                          </span>
                          <span aria-hidden>•</span>
                          <span data-testid={`progress-task-level-${t.scenario_id}-${t.attempt_level}`}>
                            {levelLabel(lang, t.attempt_level || "basic")}
                          </span>
                          {t.finished_at && (
                            <>
                              <span aria-hidden>•</span>
                              <span data-testid={`progress-task-date-${t.scenario_id}-${t.attempt_level}`}>
                                {new Date(t.finished_at).toLocaleDateString(lang === "en" ? "en-US" : "es")}
                              </span>
                            </>
                          )}
                          {t.attempts_saved > 1 && (
                            <>
                              <span aria-hidden>•</span>
                              <span>{tr("progressAttemptsSaved").replace("{n}", t.attempts_saved)}</span>
                            </>
                          )}
                        </div>
                        {(t.status === "partially_demonstrated" || t.status === "not_enough_evidence") &&
                          (lang === "en" ? t.missing_checkpoints_en : t.missing_checkpoints_es)?.length > 0 && (
                            <div
                              className="mt-1 text-[11px] text-[rgb(var(--wpe-muted))]"
                              data-testid={`progress-task-missing-${t.scenario_id}-${t.attempt_level}`}
                            >
                              <span className="font-medium">{tr("progressMissing")}: </span>
                              {(lang === "en" ? t.missing_checkpoints_en : t.missing_checkpoints_es)
                                .filter(Boolean)
                                .slice(0, 2)
                                .join(" · ")}
                            </div>
                          )}
                      </div>
                      <ChevronRight size={14} className="text-[rgb(var(--wpe-muted))] mt-0.5 flex-shrink-0" />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {sessions.map((s) => (
          <div key={s.id} className="wpe-card space-y-2" data-testid={`history-item-${s.id}`}>
            <div className="flex items-start justify-between gap-2">
              <button
                type="button"
                onClick={() => (s.feedback ? navigate(`/feedback/${s.id}`) : navigate(`/session/${s.id}`))}
                className="text-left flex-1 min-w-0"
                data-testid={`history-open-${s.id}`}
              >
                <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
                  {lang === "en" ? s.scenario?.title_en : s.scenario?.title_es}
                </div>
                <div className="text-[11px] text-[rgb(var(--wpe-muted))]">
                  {new Date(s.created_at).toLocaleString(lang === "en" ? "en-US" : "es")}
                </div>
              </button>
              <button
                type="button"
                onClick={() => setConfirmDelId(s.id)}
                className="wpe-ghost text-[11px]"
                data-testid={`history-delete-${s.id}`}
              >
                <Trash2 size={14} /> {tr("deletePractice")}
              </button>
            </div>
            <div className="flex items-center justify-between">
              <span
                className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full ${
                  (s.status === "finished" || s.completed)
                    ? "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]"
                    : "bg-[rgb(var(--wpe-subtle))] text-[rgb(var(--wpe-muted))]"
                }`}
              >
                {s.status === "finished" || s.completed ? tr("progressLifecycleFinished") : tr("progressLifecycleInProgress")}
              </span>
              <ChevronRight size={16} className="text-[rgb(var(--wpe-muted))]" />
            </div>
          </div>
        ))}

        <PilotDisclaimer />

        <div className="wpe-card space-y-2 border-dashed border-[rgb(220,38,38,0.3)]">
          <h2 className="wpe-eyebrow flex items-center gap-1">
            <AlertTriangle size={12} /> {tr("dangerZoneTitle")}
          </h2>
          <button
            type="button"
            onClick={() => setConfirmDelAccount(true)}
            className="text-[13px] text-[rgb(var(--wpe-danger))] font-semibold underline"
            data-testid="delete-account-btn"
          >
            {tr("deleteAccount")}
          </button>
        </div>

        {confirmDelId && (
          <ConfirmModal
            title={tr("confirmDelete")}
            onCancel={() => setConfirmDelId(null)}
            onConfirm={doDeleteSession}
            testid="confirm-delete-session"
            tr={tr}
          />
        )}
        {confirmDelAccount && (
          <ConfirmModal
            title={tr("confirmDeleteAccount")}
            onCancel={() => setConfirmDelAccount(false)}
            onConfirm={doDeleteAccount}
            testid="confirm-delete-account"
            tr={tr}
          />
        )}
      </section>
    </AppShell>
  );
}

const StatCell = ({ label, value, testid }) => (
  <div className="rounded-lg bg-[rgb(var(--wpe-subtle))] py-2 px-1" data-testid={testid}>
    <div className="text-[18px] font-bold text-[rgb(var(--wpe-navy))]">{value}</div>
    <div className="text-[10px] text-[rgb(var(--wpe-muted))]">{label}</div>
  </div>
);

const StatusIcon = ({ status }) => {
  if (status === "demonstrated")
    return <CheckCircle2 size={16} className="text-[rgb(var(--wpe-teal))] mt-0.5 flex-shrink-0" />;
  if (status === "partially_demonstrated")
    return <CircleDashed size={16} className="text-[rgb(var(--wpe-teal))] mt-0.5 flex-shrink-0" />;
  return <Circle size={16} className="text-[rgb(var(--wpe-muted))] mt-0.5 flex-shrink-0" />;
};

const ConfirmModal = ({ title, onCancel, onConfirm, testid, tr }) => (
  <div
    role="dialog"
    aria-modal="true"
    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-6"
    data-testid={testid}
  >
    <div className="wpe-card max-w-sm w-full space-y-3">
      <h3 className="text-[15px] font-semibold text-[rgb(var(--wpe-navy))]">{title}</h3>
      <div className="flex gap-2">
        <button className="wpe-secondary flex-1" onClick={onCancel} data-testid={`${testid}-cancel`}>
          {tr("no")}
        </button>
        <button className="wpe-cta flex-1" onClick={onConfirm} data-testid={`${testid}-confirm`}>
          {tr("yes")}
        </button>
      </div>
    </div>
  </div>
);
